/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AdminCustomerSummary } from './customerTypes'

const log = logger({ category: 'useAdminCustomers' })

export interface AdminCustomersState {
  customers: AdminCustomerSummary[]
  isLoading: boolean
  error: string
  /** Display name for a customer id, or a placeholder when it is not in the table. */
  customerName: (customerId: number | null | undefined) => string
}

/**
 * The customers the admin pages pick from — the Timescale `customer` table, not the
 * Fresh company list. Every customer we serve is in this table (the data pipeline
 * adds them), so it is the one an Insights profile should point at.
 */
export const useAdminCustomers = (): AdminCustomersState => {
  const { api } = useApi()
  const [customers, setCustomers] = useState<AdminCustomerSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminCustomerSummary[]>({
          path: '/app/admin/customers',
          origin: 'useAdminCustomers',
        })
        if (cancelled) return
        if (response.error) {
          setError(response.message || 'Could not load companies')
        } else {
          setError('')
          setCustomers(response.data ?? [])
        }
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load companies')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api])

  const customerName = useCallback(
    (customerId: number | null | undefined): string => {
      if (customerId === null || customerId === undefined) return ''
      return customers.find((c) => c.customerId === customerId)?.name ?? `company #${String(customerId)}`
    },
    [customers]
  )

  return { customers, isLoading, error, customerName }
}
